import React, { useState, useCallback } from 'react';
import {
  PlusIcon,
  TrashIcon,
  BoltIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'greater_than', label: 'is greater than' },
  { value: 'less_than', label: 'is less than' },
  { value: 'greater_equal', label: 'is greater than or equal to' },
  { value: 'less_equal', label: 'is less than or equal to' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
  { value: 'in_array', label: 'is one of' },
  { value: 'not_in_array', label: 'is not one of' }
];

const ACTIONS = [
  { value: 'show_field', label: 'Show field' },
  { value: 'hide_field', label: 'Hide field' },
  { value: 'require_field', label: 'Make field required' },
  { value: 'unrequire_field', label: 'Make field optional' },
  { value: 'set_value', label: 'Set field value' },
  { value: 'set_options', label: 'Change field options' },
  { value: 'enable_field', label: 'Enable field' },
  { value: 'disable_field', label: 'Disable field' },
  { value: 'set_placeholder', label: 'Set placeholder text' },
  { value: 'set_help_text', label: 'Set help text' }
];

const RuleBuilder = ({ rule, fields, onUpdate, onDelete, readOnly }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const updateCondition = useCallback((updates) => {
    onUpdate({
      ...rule,
      condition: { ...rule.condition, ...updates }
    });
  }, [rule, onUpdate]);

  const addAction = useCallback(() => {
    const newAction = {
      type: 'show_field',
      target: fields[0]?.id || '',
    };
    
    onUpdate({
      ...rule,
      actions: [...(rule.actions || []), newAction]
    });
  }, [rule, onUpdate, fields]);

  const updateAction = useCallback((index, updates) => {
    const updatedActions = (rule.actions || []).map((action, i) =>
      i === index ? { ...action, ...updates } : action
    );
    
    onUpdate({
      ...rule,
      actions: updatedActions
    });
  }, [rule, onUpdate]);

  const removeAction = useCallback((index) => {
    const updatedActions = (rule.actions || []).filter((_, i) => i !== index);
    onUpdate({
      ...rule,
      actions: updatedActions
    });
  }, [rule, onUpdate]);

  const targetField = fields.find(f => f.id === rule.condition?.field);
  const needsValue = rule.condition?.operator && !['is_empty', 'is_not_empty'].includes(rule.condition.operator);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
      {/* Rule Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          {isExpanded ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          )}
          <BoltIcon className="h-4 w-4 text-purple-500" />
          <span>Conditional Rule</span>
        </button>
        
        {!readOnly && (
          <button
            onClick={() => onDelete(rule.id)}
            className="p-1 text-red-500 hover:text-red-700"
            title="Delete rule"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {isExpanded && (
        <>
          {/* Condition */}
          <div className="space-y-3">
            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              When
            </h5>
            
            <div className="grid grid-cols-1 gap-2">
              {/* Field Selection */}
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-500 dark:text-gray-400">If</span>
                <select
                  value={rule.condition?.field || ''}
                  onChange={(e) => updateCondition({ field: e.target.value })}
                  disabled={readOnly}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select field</option>
                  {fields.map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Operator Selection */}
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-500 dark:text-gray-400 w-6"></span>
                <select
                  value={rule.condition?.operator || ''}
                  onChange={(e) => updateCondition({ operator: e.target.value })}
                  disabled={readOnly}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select operator</option>
                  {OPERATORS.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Value Input */}
              {needsValue && (
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-gray-500 dark:text-gray-400 w-6"></span>
                  {targetField && ['select', 'radio'].includes(targetField.type) ? (
                    <select
                      value={rule.condition?.value || ''}
                      onChange={(e) => updateCondition({ value: e.target.value })}
                      disabled={readOnly}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select value</option>
                      {targetField.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={rule.condition?.value || ''}
                      onChange={(e) => updateCondition({ value: e.target.value })}
                      readOnly={readOnly}
                      placeholder="Enter value"
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Then
              </h5>
              {!readOnly && (
                <button
                  onClick={addAction}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-500"
                >
                  <PlusIcon className="h-3 w-3 mr-1" />
                  Add Action
                </button>
              )}
            </div>

            {rule.actions?.length > 0 && (
              <div className="space-y-2">
                {rule.actions.map((action, index) => (
                  <ActionBuilder
                    key={index}
                    action={action}
                    fields={fields}
                    onUpdate={(updates) => updateAction(index, updates)}
                    onDelete={() => removeAction(index)}
                    readOnly={readOnly}
                  />
                ))}
              </div>
            )}

            {(!rule.actions || rule.actions.length === 0) && (
              <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                No actions defined. Click "Add Action" to add one.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ActionBuilder = ({ action, fields, onUpdate, onDelete, readOnly }) => {
  const actionType = ACTIONS.find(a => a.value === action.type);
  const needsTarget = !['set_value'].includes(action.type);
  const needsValue = ['set_value', 'set_placeholder', 'set_help_text'].includes(action.type);

  return (
    <div className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
      {/* Action Type */}
      <select
        value={action.type || ''}
        onChange={(e) => onUpdate({ type: e.target.value })}
        disabled={readOnly}
        className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
      >
        {ACTIONS.map((actionType) => (
          <option key={actionType.value} value={actionType.value}>
            {actionType.label}
          </option>
        ))}
      </select>

      {/* Target Field */}
      {needsTarget && (
        <select
          value={action.target || ''}
          onChange={(e) => onUpdate({ target: e.target.value })}
          disabled={readOnly}
          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
        >
          <option value="">Select field</option>
          {fields.map((field) => (
            <option key={field.id} value={field.id}>
              {field.label}
            </option>
          ))}
        </select>
      )}

      {/* Value Input */}
      {needsValue && (
        <input
          type="text"
          value={action.value || ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          readOnly={readOnly}
          placeholder="Enter value"
          className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
        />
      )}

      {/* Delete Action */}
      {!readOnly && (
        <button
          onClick={onDelete}
          className="p-1 text-red-500 hover:text-red-700"
          title="Delete action"
        >
          <TrashIcon className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

const ConditionalLogicPanel = ({ 
  fields, 
  rules, 
  onAddRule, 
  onUpdateRule, 
  onDeleteRule, 
  readOnly = false 
}) => {
  const handleAddRule = useCallback(() => {
    if (!readOnly && onAddRule) {
      const newRule = {
        condition: {
          field: '',
          operator: 'equals',
          value: ''
        },
        actions: []
      };
      onAddRule(newRule);
    }
  }, [onAddRule, readOnly]);

  if (fields.length === 0) {
    return (
      <div className="text-center py-12">
        <BoltIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
          No fields available
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Add some fields to the form first, then you can create conditional logic rules.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
            Conditional Logic
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Show/hide fields based on user input
          </p>
        </div>
        
        {!readOnly && (
          <button
            onClick={handleAddRule}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-500"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Rule
          </button>
        )}
      </div>

      {rules.length > 0 ? (
        <div className="space-y-4">
          {rules.map((rule) => (
            <RuleBuilder
              key={rule.id}
              rule={rule}
              fields={fields}
              onUpdate={(updatedRule) => onUpdateRule(rule.id, updatedRule)}
              onDelete={onDeleteRule}
              readOnly={readOnly}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <BoltIcon className="mx-auto h-8 w-8 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No conditional rules
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {readOnly 
              ? 'This form has no conditional logic configured.'
              : 'Create rules to show/hide fields based on user input.'
            }
          </p>
          {!readOnly && (
            <div className="mt-4">
              <button
                onClick={handleAddRule}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create First Rule
              </button>
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-blue-800 dark:text-blue-200">
              Conditional Logic Tips
            </p>
            <ul className="text-xs text-blue-700 dark:text-blue-300 mt-1 space-y-1">
              <li>• Use "equals" for exact matches with dropdown/radio values</li>
              <li>• Use "contains" for partial text matching</li>
              <li>• Chain multiple actions in a single rule</li>
              <li>• Test your logic in the preview tab</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConditionalLogicPanel;